import * as d3 from 'd3';
import * as _ from 'lodash';

import { ITreeItem, IGroupValuesData, State, IQualityOverviewMerged, IQualityOverviewFormat } from '../../models';
import { errorTextColor, guidEmpty, missingTextColor, successTextColor, warningTextColor } from '../../common/constants';

export interface IQualityAnalysisHelper {
    mergeData(groups: ITreeItem[], data: IGroupValuesData, states: State[], redirectBuilder: (string, any) => () => void)
        : IQualityOverviewMerged[];
}

export class QualityAnalysisHelper implements IQualityAnalysisHelper {
    static serviceId = 'qualityAnalysisHelper';

    static $inject = [];

    private colorScale = d3.scale.ordinal<string, string>()
        .range([missingTextColor, successTextColor, warningTextColor, errorTextColor])
        .domain(['-2', '0', '1', '2']);

    private labelScale = d3.scale.ordinal<string, string>()
        .range(['Missing', 'OK', 'Warning', 'Error'])
        .domain(['-2', '0', '1', '2']);

    mergeData(groups: ITreeItem[], data: IGroupValuesData, states: State[], redirectBuilder: (string, any) => () => void)
        : IQualityOverviewMerged[] {
        try {
            if (!groups) {
                groups = [];
            }
            return groups.map(d => {
                const currentData = data[d.guid];
                const stateParams = {
                    groupGuid: d.guid,
                    selectedGroup: d
                };
                const mappedData: { data: IQualityOverviewFormat[] }[] = [
                    currentData
                        ? this.mapStatus(currentData.measured, redirectBuilder, 'qualityAnalysis.tagsInError', stateParams)
                        : { data: [] },
                    currentData
                        ? this.mapStatus(currentData.calculated, redirectBuilder, 'qualityAnalysis.calculated', stateParams)
                        : { data: [] },
                    currentData
                        ? this.mapStatus(currentData.validated, redirectBuilder, 'qualityAnalysis.validated', stateParams)
                        : { data: [] },
                    currentData
                        ? this.mapStates(currentData.states, states, d.guid, redirectBuilder)
                        : { data: [] }
                ];
                return {
                    group: d,
                    data: mappedData
                };
            });
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    private mapStates(values: { [stateGuid: number]: number }, states: State[], groupGuid: string, redirectBuilder: (string, any) => () => void)
        : { data: IQualityOverviewFormat[], redirect?: () => void } {
        try {
            const result: IQualityOverviewFormat[] = [];
            let tot = 0;
            for (const key of Object.keys(values)) {
                let state = _.find(states, s => s.guid === key);
                if (!state) {
                    state = {
                        name: 'Not calculated',
                        guid: guidEmpty,
                        markerColorString: missingTextColor
                    } as any;
                }
                result.push({
                    color: state.markerColorString,
                    name: state.name,
                    value: values[key],
                    start: tot
                });
                tot += values[key];
            }
            return { data: result, redirect: redirectBuilder('group.rules', { groupGuid }) };
        } catch (error) {
            console.error(error);
            return { data: [] };
        }
    }

    private mapStatus(statuses: { [statusId: number]: number },
        redirectBuilder: (string, any) => () => void,
        state?: string, stateParams?: { [index: string]: any })
        : { data: IQualityOverviewFormat[], redirect?: () => void } {
        const measured: IQualityOverviewFormat[] = [];
        let tot = 0;
        for (const key of Object.keys(statuses)) {
            measured.push({
                color: this.colorScale(key),
                name: this.labelScale(key),
                value: statuses[key],
                start: tot
            });
            tot += statuses[key];
        }
        return { data: measured, redirect: state ? redirectBuilder(state, stateParams) : null };
    }
}
